import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES, USER_STATUSES } from "models";
import { useDeleteUserMutation, useSetDpoMutation } from "modules/UsersDashboard/components/EditUserModal/hooks";
import { useUserDetailsQuery } from "modules/UsersDashboard/hooks";

type EditUserActionsProps = {
  formId: string;
  userId: number | null;
  isDpoUser: boolean;
  isPending: boolean;
  canEditEntireForm: boolean;
  canUpdateUsers: boolean;
  onClose: () => void;
};

export const EditUserActions = ({ formId, userId, isDpoUser, isPending, canEditEntireForm, canUpdateUsers, onClose }: EditUserActionsProps) => {
  const { t } = useTranslation();

  const [showSetDpo, setShowSetDpo] = useState(false);
  const [showDeleteUser, setShowDeleteUser] = useState(false);

  const { data: details } = useUserDetailsQuery(userId);
  const { mutateAsync: deleteUser, isPending: isDeleteUserPending } = useDeleteUserMutation();
  const { mutateAsync: setUserDpoMutation, isPending: isSetDpoPending } = useSetDpoMutation();

  const handleOpenDpoModal = () => setShowSetDpo(true);
  const handleCloseDpoModal = () => setShowSetDpo(false);
  const handleOpenDeleteModal = () => setShowDeleteUser(true);
  const handleCloseDeleteModal = () => setShowDeleteUser(false);

  const handleDeleteUser = async () => {
    if (!userId) return;
    await deleteUser(userId);
    handleCloseDeleteModal();
    onClose();
  };

  const handleSetDpo = async () => {
    if (!userId) return;

    await setUserDpoMutation({ location_id: details?.dealer_location_id ?? -1, user_id: userId });
    handleCloseDpoModal();
    onClose();
  };

  const handleOpenDpoConfirmation = () => details?.status === USER_STATUSES.UserActive && !isDpoUser && handleOpenDpoModal();

  return (
    <>
      <div className={`delete-and-dpo-container ${canEditEntireForm ? "with-actions" : "no-actions"}`}>
        {canEditEntireForm && (
          <div className="action-buttons-container actions-left">
            <Can I="delete" the="users">
              <Button color="red" type="button" onClick={handleOpenDeleteModal}>
                {t("v8_delete", "Delete")}
                <Icon className="trash" />
              </Button>
            </Can>

            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.DataOfficer}>
              <Button
                color="blue"
                type="button"
                className={`${(details?.status === USER_STATUSES.UserInactive || isDpoUser) && "dpo-disabled"}`}
                onClick={handleOpenDpoConfirmation}
              >
                {t("v8_set_as_dpo", "Set as DPO")}
              </Button>
            </Can>
          </div>
        )}

        <div className="action-buttons-container actions-right">
          <Button className="light" type="button" onClick={onClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Button>
          <Button type="submit" form={formId} color="green" disabled={isPending || !canUpdateUsers} loading={isPending}>
            {t("v8_save", "Save")}
            <Icon className="check" />
          </Button>
        </div>
      </div>

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={showDeleteUser}
        handleCancel={handleCloseDeleteModal}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={handleDeleteUser}
        isLoading={isDeleteUserPending}
      />
      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Warning}
        isOpen={showSetDpo}
        handleCancel={handleCloseDpoModal}
        confirmMsg={t("v8_protection_officer_confirmation", "Are you sure you want to appoint this person as data protection officer?")}
        handleConfirm={handleSetDpo}
        isLoading={isSetDpoPending}
      />
    </>
  );
};
